import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import PropTypes from 'prop-types';

import Box from 'shopper/components/Box';
import Heading from 'shopper/components/Heading';
import Link from 'shopper/components/Link';
import Tag from 'shopper/components/Tag/V1';
import Text from 'shopper/components/Text';

import UserAvatar from 'components/UserAvatar';

import useMediaQuery from 'hooks/useMediaQuery';

import { formatDateByDayFullMonthYearHourMinute } from 'lib/date';
import { sendEvent } from 'lib/gtag';
import { toThreadCategoryLink, toUserProfileLink } from 'lib/links';
import placeholder from 'lib/placeholder';
import { isGenieUser, toUsernameWithAtSign } from 'lib/user';
import { scrollToElement } from 'lib/utils';

import { useUser } from 'providers/UserProvider';

import ThreadMainContentHeadFooter from './MainContentHeadFooter';

const Badge = dynamic(() => import('shopper/components/Badge'), {
  ssr: false,
});
const ThreadOptionsDropdown = dynamic(() => import('./ThreadOptionsDropdown'), {
  ssr: false,
});
const ThreadStatusAlert = dynamic(() => import('./StatusAlert'), {
  ssr: false,
});
const ADDITIONAL_DISTANCE_FROM_TOP = 16;
const ThreadMainContentHead = ({
  commentsRef,
  gaEventCategory,
  threadCategoryName,
  threadCategorySlug,
  threadComments,
  threadId,
  threadLikes,
  threadPublished,
  threadReviewed,
  threadSlug,
  threadSolved: isThreadSolved,
  threadStatusName,
  threadTitle,
  threadViews,
  userId,
  userLevel,
  userName,
  userPhoto,
  userTypeName,
  userUsername,
  onAddLike,
}) => {
  const { isLg } = useMediaQuery();
  const { LoggedInUser, loadingLoggedInUser } = useUser();
  const onCommentsButtonClickHandler = () => {
    sendEvent({ action: 'reply_post', category: gaEventCategory });
    scrollToElement(
      commentsRef.current.offsetTop - ADDITIONAL_DISTANCE_FROM_TOP,
      isLg
    );
  };
  return (
    <>
      {!loadingLoggedInUser &&
        LoggedInUser?.canSeeThreadStatusInfo(userId, threadStatusName) && (
          <ThreadStatusAlert statusName={threadStatusName} userId={userId} />
        )}
      <Box className="relative flex-row rounded-b-none border-b-3 border-primary-300 py-4 md:px-6 md:py-6">
        <div className="flex w-full flex-wrap justify-between">
          <div className="flex w-full items-center px-4 md:px-0">
            <UserAvatar
              size="size2"
              userId={userId}
              userLevel={userLevel}
              userName={userName}
              userPhoto={userPhoto}
              userTypeName={userTypeName}
              userUsername={userUsername}
              asLink
            />
            <div className="ml-6 w-full pb-2 md:border-b md:border-solid md:border-neutral-high-200 dark:md:border-neutral-low-200">
              {(threadCategoryName || isThreadSolved) && (
                <div className="mb-4 flex w-full items-center justify-between">
                  <div className="flex items-center">
                    {threadCategoryName && (
                      <Tag
                        as="a"
                        href={toThreadCategoryLink(threadCategorySlug)}
                        filled
                      >
                        {threadCategoryName}
                      </Tag>
                    )}
                    {isThreadSolved && (
                      <Badge className="ml-2" size="size2" type="success">
                        {placeholder('descriptions.threadSolved')}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              <div className="flex flex-col items-start justify-between">
                <div className="flex">
                  <Heading as="h1" size="size5">
                    {threadTitle}
                  </Heading>
                  {threadReviewed && (
                    <Text as="span" className="ml-2 italic">
                      (Editado)
                    </Text>
                  )}
                </div>
                <Text
                  className="mt-2 flex items-center justify-center"
                  size="size2"
                >
                  por
                  {isGenieUser(userId) ? (
                    <Text
                      as="span"
                      className="text-neutral-low-100 dark:text-neutral-high-100"
                    >
                      <b>{` ${userName}`}</b>
                      {` (${toUsernameWithAtSign(userUsername)})`}
                    </Text>
                  ) : (
                    <NextLink
                      href={toUserProfileLink(userUsername)}
                      prefetch={false}
                      passHref
                    >
                      <Link size="size2" type="secondary">
                        <b>{` ${userName}`}</b>
                        {` (${toUsernameWithAtSign(userUsername)})`}
                      </Link>
                    </NextLink>
                  )}
                </Text>
                <Text className="mt-2" size="size2">
                  {formatDateByDayFullMonthYearHourMinute(threadPublished)}
                </Text>
                {LoggedInUser?.canMarkThreadAsSolved(userId) && (
                  <ThreadOptionsDropdown
                    threadId={threadId}
                    threadSlug={threadSlug}
                  />
                )}
              </div>
            </div>
          </div>
          <ThreadMainContentHeadFooter
            gaEventCategory={gaEventCategory}
            threadComments={threadComments}
            threadId={threadId}
            threadLikes={threadLikes}
            threadViews={threadViews}
            onAddLike={onAddLike}
            onCommentsButtonClick={onCommentsButtonClickHandler}
          />
        </div>
      </Box>
    </>
  );
};

ThreadMainContentHead.propTypes = {
  commentsRef: PropTypes.shape(),
  gaEventCategory: PropTypes.string.isRequired,
  threadCategoryName: PropTypes.string.isRequired,
  threadCategorySlug: PropTypes.string.isRequired,
  threadComments: PropTypes.number.isRequired,
  threadId: PropTypes.number.isRequired,
  threadLikes: PropTypes.number.isRequired,
  threadPublished: PropTypes.string.isRequired,
  threadStatusName: PropTypes.string.isRequired,
  threadSolved: PropTypes.bool.isRequired,
  threadTitle: PropTypes.string.isRequired,
  threadViews: PropTypes.number.isRequired,
  userId: PropTypes.number.isRequired,
  userLevel: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string.isRequired,
  userUsername: PropTypes.string.isRequired,
  onAddLike: PropTypes.func.isRequired,
};

export default ThreadMainContentHead;
